import type { ResourceType } from './enums'
import type { BasicResourceItem, ResourceItem } from './types'

/**
 * 递归过滤菜单, 返回树结构，如果父节点reType !== sourceType, 父节点会被去除，不管父节点里的子节
 * 点是否符合条件
 *
 * @param permissions 资源列表
 * @param type 资源类型
 * @return 符合条件的资源树
 *
 * @description 该函数会递归遍历资源树，筛选出符合指定类型的资源，并保持树结构返回。只有当资源的类型与指定类型匹配时，该资源才会被包含在结果中。如果一个资源的类型不匹配，即使它有子节点符合条件，也会被整个过滤掉。这意味着只有直接符合条件的资源及其符合条件的子节点会被保留在最终的树结构中。
 *
 * @example
 * ```
 * const permissions: ResourceItem[] = [
 *   {
 *     reId: 'module-1',
 *     reType: ResourceType.MODULE,
 *     hasChild: true,
 *     childList: [
 *       {
 *         reId: 'menu-under-module',
 *         reType: ResourceType.MENU,
 *         hasChild: false,
 *         childList: [],
 *       },
 *     ],
 *   },
 *   {
 *     reId: 'menu-1',
 *     reType: ResourceType.MENU,
 *     hasChild: true,
 *     childList: [
 *       {
 *         reId: 'button-1',
 *         reType: ResourceType.BUTTON,
 *         hasChild: false,
 *         childList: [],
 *       },
 *       {
 *         reId: 'menu-1-1',
 *         reType: ResourceType.MENU,
 *         hasChild: false,
 *         childList: [],
 *       },
 *     ],
 *   },
 * ]
 *
 * const result = filterResourceTree(permissions, ResourceType.MENU)
 * ```
 *
 * result 将会是:
 * ```
 * [
 *   {
 *     reId: 'menu-1',
 *     reType: ResourceType.MENU,
 *     hasChild: true,
 *     childList: [
 *       {
 *         reId: 'menu-1-1',
 *         reType: ResourceType.MENU,
 *         hasChild: false,
 *         childList: [],
 *       },
 *     ],
 *   },
 * ]
 * ```
 * 注意：如果一个资源的类型不匹配，即使它有子节点符合条件，也会被整个过滤掉。这意味着只有直接符合条件的资源及其符合条件的子节点会被保留在最终的树结构中。
 */
export function filterResourceTree(
  permissions: ResourceItem[],
  type: ResourceType,
): ResourceItem[] {
  const newArr: ResourceItem[] = []

  for (const item of permissions) {
    if (item.reType === type) {
      const { childList, hasChild, ...restItem } = item

      const clonedItem: ResourceItem = Object.assign(restItem, {
        hasChild: false,
        childList: [],
      })

      if (hasChild && !!childList?.length) {
        const filteredChildren = filterResourceTree(childList, type)

        clonedItem.childList = filteredChildren
        clonedItem.hasChild = filteredChildren.length > 0
      }

      newArr.push(clonedItem)
    }
  }

  return newArr
}

/**
 * 递归过滤资源，并将符合条件的资源扁平化返回
 *
 * @param permissions 资源列表
 * @param type 资源类型
 * @returns 符合条件的资源列表
 *
 * @description 该函数会递归遍历资源树，筛选出符合指定类型的资源，并将它们扁平化为一个数组返回。无论资源是否有子节点，只要它的类型符合条件，就会被包含在结果中。同时，如果资源有子节点，也会继续递归检查子节点，确保所有符合条件的资源都被收集到结果中。
 *
 * @example
 * ```
 * const permissions: ResourceItem[] = [
 *   {
 *     reId: 'module-1',
 *     reType: ResourceType.MODULE,
 *     hasChild: true,
 *     childList: [
 *       {
 *         reId: 'menu-under-module',
 *         reType: ResourceType.MENU,
 *         hasChild: false,
 *         childList: [],
 *       },
 *     ],
 *   },
 *   {
 *     reId: 'menu-1',
 *     reType: ResourceType.MENU,
 *     hasChild: true,
 *     childList: [
 *       {
 *         reId: 'button-1',
 *         reType: ResourceType.BUTTON,
 *         hasChild: false,
 *         childList: [],
 *       },
 *       {
 *         reId: 'menu-1-1',
 *         reType: ResourceType.MENU,
 *         hasChild: false,
 *         childList: [],
 *       },
 *     ],
 *   },
 * ]
 *
 * const result = filterAndFlatResources(permissions, ResourceType.MENU)
 * ```
 *
 * result 将会是:
 * ```
 * [
 *   {
 *     reId: 'menu-under-module',
 *     reType: ResourceType.MENU,
 *   },
 *   {
 *     reId: 'menu-1',
 *     reType: ResourceType.MENU,
 *   },
 *   {
 *     reId: 'menu-1-1',
 *     reType: ResourceType.MENU,
 *   },
 * ]
 * ```
 * 注意：返回的资源对象中不包含 hasChild 和 childList 属性，因为结果是扁平化的数组，每个资源对象只包含基本属性。
 */
export function filterAndFlatResources(
  permissions: ResourceItem[],
  type: ResourceType,
): BasicResourceItem[] {
  const newArr: BasicResourceItem[] = []

  for (const item of permissions) {
    const { childList = [], hasChild, ...restItem } = item

    if (item.reType === type) {
      newArr.push(restItem)
    }

    if (hasChild && !!childList?.length) {
      const children = filterAndFlatResources(childList, type)

      newArr.push(...children)
    }
  }

  return newArr
}
